import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Intent, Tag } from '@blueprintjs/core';

import { ShowHide } from 'components/layout';
import { Tooltip } from 'components/tooltip';

import { formatValue } from './diffViewerFormatting';

const notAvailableText = "—";
const notMeasuredText = "not measured in UnderTest";
const coverageUnavailableText = "coverage unavailable";
const gateFailedText = "could not be loaded";

// Each figure takes keyboard focus, so its explanation opens on focus as well as on hover. The tooltip
// is not announced, so each figure also carries its explanation as visually hidden text for screen readers.
const gateTooltips = {
    stillOpen: "Differences nobody has explained yet, plus any signed off as a regression. Auto-labelled and known differences don't count, the filters never change it, and drivers on an excluded account are left out.",
    provisional: "Seen once and not yet repeated by a later run. Still counted in Still open.",
    analysed: "Drivers with at least one completed comparison.",
    environmental: "Drivers not compared because of an infrastructure problem such as a timeout. They are retried.",
    genuine: "Drivers not compared because of their data, or with no reason recorded.",
    waiting: "Drivers queued or being compared now."
};

function formatCount(value) {
    if (value == null) {
        return notAvailableText;
    }

    return formatValue(value);
}

function formatStillOpen(gate, gateFailed) {
    if (gateFailed) {
        return gateFailedText;
    }

    return formatCount(gate?.stillOpen);
}

function hasDriverFigures(gate) {
    return gate != null
        && gate.analysedDrivers != null
        && gate.notAnalysedEnvironmental != null
        && gate.notAnalysedGenuine != null
        && gate.waiting != null;
}

/**
 * The exit-gate figure, read only from the gate the endpoint reports and never from the differences on
 * screen, so no filter or toggle on the page can move it. Coverage for a chosen period has its own line.
 */
export function DiffViewerGatePanel(
    {
        gate = null,
        gateFailed = false,
        coverage = null,
        coverageFailed = false,
        coverageMeasured = true,
        periodChosen = false,
        loading = false
    }
) {
    return (
        <div id="rules-engine-diff-gate" className={classNames("spacer-bottom", { "bp6-skeleton": loading })}>
            <h3 id="rules-engine-diff-gate-heading">Exit gate</h3>

            <p id="rules-engine-diff-gate-still-open" className="inline-items">
                <Tooltip content={gateTooltips.stillOpen}>
                    <span id="rules-engine-diff-gate-still-open-figure">
                        {`Still open: ${formatStillOpen(gate, gateFailed)}`}
                        <span className="rules-engine-diff-visually-hidden">{`. ${gateTooltips.stillOpen}`}</span>
                    </span>
                </Tooltip>
                <ShowHide
                    evaluator={!gateFailed && gate?.stillOpenProvisional != null}
                    show={(
                        <Tooltip content={gateTooltips.provisional}>
                            <span id="rules-engine-diff-gate-provisional">
                                {`of which ${formatCount(gate?.stillOpenProvisional)} provisional`}
                                <span className="rules-engine-diff-visually-hidden">{`. ${gateTooltips.provisional}`}</span>
                            </span>
                        </Tooltip>
                    )}
                />
            </p>

            <ShowHide
                evaluator={!gateFailed && gate != null}
                show={(
                    <p id="rules-engine-diff-gate-coverage" className="inline-items">
                        <ShowHide
                            evaluator={hasDriverFigures(gate)}
                            show={(
                                <span className="inline-items">
                                    <Tooltip content={gateTooltips.analysed}>
                                        <span id="rules-engine-diff-gate-analysed">
                                            {`Analysed ${formatCount(gate?.analysedDrivers)}`}
                                            <span className="rules-engine-diff-visually-hidden">{`. ${gateTooltips.analysed}`}</span>
                                        </span>
                                    </Tooltip>
                                    <Tooltip content={gateTooltips.environmental}>
                                        <span id="rules-engine-diff-gate-environmental">
                                            {`· not analysed ${formatCount(gate?.notAnalysedEnvironmental)} environmental /`}
                                            <span className="rules-engine-diff-visually-hidden">{`. ${gateTooltips.environmental}`}</span>
                                        </span>
                                    </Tooltip>
                                    <Tooltip content={gateTooltips.genuine}>
                                        <span id="rules-engine-diff-gate-genuine-figure">
                                            <Tag id="rules-engine-diff-gate-genuine" intent={Intent.WARNING} minimal>
                                                {`${formatCount(gate?.notAnalysedGenuine)} genuine`}
                                            </Tag>
                                            <span className="rules-engine-diff-visually-hidden">{`. ${gateTooltips.genuine}`}</span>
                                        </span>
                                    </Tooltip>
                                    <Tooltip content={gateTooltips.waiting}>
                                        <span id="rules-engine-diff-gate-waiting">
                                            {`· still waiting ${formatCount(gate?.waiting)}`}
                                            <span className="rules-engine-diff-visually-hidden">{`. ${gateTooltips.waiting}`}</span>
                                        </span>
                                    </Tooltip>
                                </span>
                            )}
                            hide={(
                                <span id="rules-engine-diff-gate-not-measured">{`Drivers ${notMeasuredText}`}</span>
                            )}
                        />
                    </p>
                )}
            />

            <ShowHide
                evaluator={periodChosen}
                show={(
                    <div id="rules-engine-diff-period">
                        <h4 id="rules-engine-diff-period-heading">Selected period</h4>
                        <ShowHide
                            evaluator={coverageMeasured}
                            show={(
                                <ShowHide
                                    evaluator={coverage != null && !coverageFailed}
                                    show={(
                                        <p id="rules-engine-diff-period-coverage" className="inline-items">
                                            <span>{`Attempted ${formatCount(coverage?.attempted)}`}</span>
                                            <span>{`· analysed ${formatCount(coverage?.analysed)}`}</span>
                                            <span>{`· with differences ${formatCount(coverage?.analysedWithDifferences)}`}</span>
                                            <span>{`· not analysed ${formatCount(coverage?.notAnalysedEnvironmental)} environmental /`}</span>
                                            <Tag id="rules-engine-diff-period-genuine" intent={Intent.WARNING} minimal>
                                                {`${formatCount(coverage?.notAnalysedGenuine)} genuine`}
                                            </Tag>
                                            <span>{`· still waiting ${formatCount(coverage?.waiting)}`}</span>
                                        </p>
                                    )}
                                    hide={(
                                        <p id="rules-engine-diff-period-coverage">{coverageFailed ? coverageUnavailableText : notAvailableText}</p>
                                    )}
                                />
                            )}
                            hide={(
                                <p id="rules-engine-diff-period-coverage">{`Drivers ${notMeasuredText}`}</p>
                            )}
                        />
                    </div>
                )}
            />
        </div>
    );
}

DiffViewerGatePanel.propTypes = {
    gate: PropTypes.object,
    gateFailed: PropTypes.bool,
    coverage: PropTypes.object,
    coverageFailed: PropTypes.bool,
    coverageMeasured: PropTypes.bool,
    periodChosen: PropTypes.bool,
    loading: PropTypes.bool
};
